'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CatalogueForm, type CatalogueFormRef } from '@/components/partials/catalogues'
import { type CreateCatalogueParams } from '@/types/catalogue'
import { useCreateCatalogue } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const catalogueFormRef = useRef<CatalogueFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createCatalogue, isPending: isLoading } = useCreateCatalogue()

  const handleSubmit = (data: CreateCatalogueParams) => {
    createCatalogue(data, {
      onSuccess: async newCatalogue => {
        setIsRedirecting(true)
        queryClient.setQueryData(['catalogue', newCatalogue.id], newCatalogue)
        await queryClient.invalidateQueries({ queryKey: ['catalogues'] })
        toast.success(t('created_successfully'))
        router.push(`/galleries/catalogues/${newCatalogue.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateCatalogueParams) => {
    createCatalogue(data, {
      onSuccess: async newCatalogue => {
        queryClient.setQueryData(['catalogue', newCatalogue.id], newCatalogue)
        await queryClient.invalidateQueries({ queryKey: ['catalogues'] })
        const detailUrl = `/galleries/catalogues/${newCatalogue.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        catalogueFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (catalogueFormRef.current) {
        catalogueFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/galleries/catalogues')
  }

  return (
    <CatalogueForm
      key={formKey}
      ref={catalogueFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
